import { Check, Loader2, LogOut, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/hooks/useBilling";

const PERKS = ["Unlimited project requests", "AI scope, price range & red flags", "Instant email alerts", "Your branded quote link"];

export const Paywall = () => {
  const { user, logout } = useAuth();
  const { busy, startCheckout, openPortal } = useBilling();
  const navigate = useNavigate();
  const sub = user.subscription;
  const trialExpired = sub.status === "trialing";
  return (
    <div className="grain min-h-screen" style={{ "--brand": user.brand_color }} data-testid="paywall">
      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
        <p className="text-xs font-semibold uppercase tracking-wider text-[color:var(--brand)]">{user.company_name}</p>
        <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-foreground" data-testid="paywall-title">
          {trialExpired ? "Your free trial has ended" : "Your subscription is inactive"}
        </h1>
        <p className="mt-3 text-base text-muted-foreground">
          Your quote link is paused and homeowners see "temporarily unavailable". Subscribe to turn it back on and see your leads.
        </p>
        <div className="mt-8 rounded-2xl border border-border bg-card p-5">
          <p className="text-4xl font-extrabold text-foreground" data-testid="paywall-price">$99<span className="text-base font-semibold text-muted-foreground">/month</span></p>
          <ul className="mt-4 space-y-2">
            {PERKS.map((p) => <li key={p} className="flex items-center gap-2 text-[15px] text-foreground"><Check className="h-4 w-4 text-emerald-600" />{p}</li>)}
          </ul>
          <button type="button" onClick={startCheckout} disabled={busy} data-testid="paywall-subscribe-button"
            className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-[color:var(--brand)] text-lg font-semibold text-white active:scale-[0.98] transition-transform disabled:opacity-50">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />} Subscribe now
          </button>
          {sub.has_billing && sub.status === "past_due" && (
            <button type="button" onClick={openPortal} disabled={busy} data-testid="paywall-manage-billing-button"
              className="mt-2 h-12 w-full rounded-xl text-sm font-semibold text-muted-foreground underline">Update payment method</button>
          )}
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4" />Secure checkout by Stripe. Cancel anytime.</p>
        </div>
        <button type="button" onClick={async () => { await logout(); navigate("/login"); }} data-testid="paywall-logout-button"
          className="mt-6 flex items-center gap-2 self-start text-sm font-semibold text-muted-foreground"><LogOut className="h-4 w-4" />Log out</button>
      </div>
    </div>
  );
};

export const TrialBanner = () => {
  const { user } = useAuth();
  const { busy, startCheckout } = useBilling();
  const sub = user.subscription;
  if (sub.status !== "trialing") return null;
  return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10" data-testid="trial-banner">
      <p className="text-sm text-amber-900 dark:text-amber-200"><span className="font-bold">{sub.trial_days_left} days</span> left in your free trial</p>
      <button type="button" onClick={startCheckout} disabled={busy} data-testid="trial-subscribe-button"
        className="h-10 shrink-0 rounded-lg bg-slate-900 px-3 text-sm font-semibold text-white disabled:opacity-50">Subscribe</button>
    </div>
  );
};
